import { formatCurrency } from "@/lib/format-data";

import { ItemType } from "@/types/enums/item-type";
import {
  PublicShopPromotion,
  PublicShopPromotionCondition,
  ShopCartLine,
  ShopItem,
  ShopShippingPolicy,
} from "@/types/shop";

export interface ShopPromotionMessage {
  id: string;
  promotionId: string;
  text: string;
  status: "progress" | "unlocked";
  /** Set when the cart qualifies for this free item and the shopper can add it. */
  giftOffer?: PublicShopPromotion;
}

export interface ShopPromotionAnnouncement {
  id: string;
  promotionId: string | null;
  offerId: string | null;
  text: string;
}

export const getShopShippingAnnouncement = (
  shipping: ShopShippingPolicy | null | undefined,
  shippingSubtotalCents = 0,
): ShopPromotionAnnouncement | null => {
  if (!shipping || shipping.freeShippingOverCents === null) return null;

  const { freeShippingOverCents } = shipping;
  const hasUnlockedFreeShipping = shippingSubtotalCents > freeShippingOverCents;
  const amountRemaining = Math.max(
    1,
    freeShippingOverCents - shippingSubtotalCents + 1,
  );
  return {
    id: "free-shipping-threshold",
    promotionId: null,
    offerId: null,
    text: hasUnlockedFreeShipping
      ? "Free shipping unlocked!"
      : `${formatCurrency(amountRemaining)} away from free shipping!`,
  };
};

const conditionQuantity = (
  condition: PublicShopPromotionCondition,
  lines: ShopCartLine[],
): number =>
  lines.reduce((sum, line) => {
    if (
      condition.itemId &&
      (!("itemId" in line) || line.itemId !== condition.itemId)
    ) {
      return sum;
    }
    return sum + line.quantity;
  }, 0);

const baseCartSubtotal = (lines: ShopCartLine[]): number =>
  lines.reduce((sum, line) => {
    if (line.kind === "custom") return sum + line.amountCents * line.quantity;
    const unitPrice =
      "expectedUnitPriceCents" in line
        ? (line.expectedUnitPriceCents ?? line.unitPriceCents)
        : line.unitPriceCents;
    return sum + unitPrice * line.quantity;
  }, 0);

const groupPromotionConditions = (
  conditions: PublicShopPromotionCondition[],
): PublicShopPromotionCondition[][] => {
  const groups = new Map<number, PublicShopPromotionCondition[]>();
  for (const condition of conditions) {
    const group = groups.get(condition.group);
    if (group) {
      group.push(condition);
    } else {
      groups.set(condition.group, [condition]);
    }
  }
  return Array.from(groups.values());
};

const conditionLabel = (condition: PublicShopPromotionCondition): string => {
  if (condition.ruleType === "MIN_SUBTOTAL") {
    return `Spend ${formatCurrency(condition.minSubtotalCents ?? 0)}`;
  }
  const quantity = condition.minQuantity ?? 1;
  if (condition.itemName) return `Buy ${quantity} ${condition.itemName}`;
  return `Buy ${quantity} ${quantity === 1 ? "item" : "items"}`;
};

export const promotionConditionSummary = (
  promotion: PublicShopPromotion,
): string | null => {
  if (!promotion.showConditionMessaging || promotion.conditions.length === 0) {
    return null;
  }

  return groupPromotionConditions(promotion.conditions)
    .map((conditions) => conditions.map(conditionLabel).join(" or "))
    .join(" and ");
};

const targetIsInCart = (
  promotion: PublicShopPromotion,
  lines: ShopCartLine[],
): boolean =>
  lines.some((line) => {
    if (promotion.targetItemIds.length > 0) {
      return "itemId" in line && promotion.targetItemIds.includes(line.itemId);
    }
    if (
      promotion.targetItemId &&
      (!("itemId" in line) || line.itemId !== promotion.targetItemId)
    ) {
      return false;
    }
    if (
      promotion.targetItemType &&
      line.itemType !== promotion.targetItemType
    ) {
      return false;
    }
    if (
      promotion.targetCategory &&
      line.snapshot.category !== promotion.targetCategory
    ) {
      return false;
    }
    return true;
  });

const promotionIsRelevant = (
  promotion: PublicShopPromotion,
  lines: ShopCartLine[],
): boolean => {
  if (promotion.conditions.some((condition) => !condition.itemId)) return true;
  if (targetIsInCart(promotion, lines)) return true;
  return promotion.conditions.some((condition) =>
    lines.some((line) => "itemId" in line && line.itemId === condition.itemId),
  );
};

export const promotionRewardLabel = (
  promotion: PublicShopPromotion,
): string | null => {
  const target = promotion.targetDescription;
  if (!target) return null;
  const quantity = promotion.quantity ? `${promotion.quantity} ` : "";
  if (promotion.percentage === 100) return `${quantity}${target} free`;
  if (promotion.percentage) {
    return `${promotion.percentage}% off ${quantity}${target}`;
  }
  if (promotion.amount) {
    return `${formatCurrency(promotion.amount)} off ${quantity}${target}`;
  }
  return null;
};

const normalizeAnnouncementText = (text: string): string =>
  text.trim().replace(/\s+/g, " ").toLowerCase();

export const getShopPromotionAnnouncements = (
  promotions: PublicShopPromotion[],
): ShopPromotionAnnouncement[] => {
  const seenText = new Set<string>();
  const announcements: ShopPromotionAnnouncement[] = [];

  for (const promotion of promotions) {
    if (promotion.giftItemName) continue;
    const description = promotion.description?.trim();
    const reward = promotionRewardLabel(promotion);
    const condition = promotionConditionSummary(promotion);

    let text: string;
    if (description) {
      text = description;
    } else if (promotion.showConditionMessaging && condition && reward) {
      text = `${condition}, get ${reward}.`;
    } else if (reward) {
      text = `${promotion.name}: ${reward}.`;
    } else {
      text = promotion.name;
    }

    const normalizedText = normalizeAnnouncementText(text);
    if (!normalizedText || seenText.has(normalizedText)) continue;
    seenText.add(normalizedText);
    announcements.push({
      id: promotion.offerId,
      promotionId: promotion.id,
      offerId: promotion.offerId,
      text,
    });
  }

  return announcements;
};

/** Free items hidden from the shop, as items for the Promotions row and the cart. */
export const giftPromotionItems = (
  promotions: PublicShopPromotion[],
): ShopItem[] => {
  const seen = new Set<string>();
  return promotions.flatMap((offer) => {
    const giftId = offer.targetItemId;
    if (!offer.giftItemName || !giftId || seen.has(giftId)) return [];
    seen.add(giftId);
    return [
      {
        id: giftId,
        name: offer.giftItemName,
        description: null,
        price: offer.giftItemPriceCents ?? 0,
        type: ItemType.PRODUCT,
        category: null,
        imageUrl: offer.giftItemImageUrl ?? null,
        availablePromotions: [offer],
      },
    ];
  });
};

const giftOfferForItem = (item: ShopItem): PublicShopPromotion | null =>
  item.availablePromotions?.find(
    (offer) => offer.giftItemName && offer.targetItemId === item.id,
  ) ?? null;

const giftConditionPhrase = (offer: PublicShopPromotion): string => {
  const [only, ...others] = offer.showConditionMessaging
    ? offer.conditions
    : [];
  if (only?.ruleType === "MIN_SUBTOTAL" && others.length === 0) {
    return `with a ${formatCurrency(only.minSubtotalCents ?? 0)} purchase`;
  }
  const summary = promotionConditionSummary(offer);
  return summary
    ? `when you ${summary.charAt(0).toLowerCase()}${summary.slice(1)}`
    : "with a qualifying purchase";
};

export interface CatalogPromotionPresentation {
  kind: "applied" | "available" | "gift";
  promotionId: string;
  name: string;
  benefit: string;
  action: string;
}

const catalogConditionSummary = (
  promotion: PublicShopPromotion,
  itemId: string,
): string | null => {
  if (!promotion.showConditionMessaging || promotion.conditions.length === 0) {
    return null;
  }

  return groupPromotionConditions(promotion.conditions)
    .map((conditions) =>
      conditions
        .map((condition) => {
          if (condition.itemId === itemId) {
            return `Buy ${condition.minQuantity ?? 1}`;
          }
          return conditionLabel(condition);
        })
        .join(" or "),
    )
    .join(" and ");
};

const catalogConditionalBenefit = (
  promotion: PublicShopPromotion,
): string | null => {
  if (promotion.percentage === 100) {
    const target = promotion.targetDescription;
    if (!target) return null;
    return `${promotion.quantity ? `${promotion.quantity} ` : ""}${target} free`;
  }
  if (promotion.percentage) return `${promotion.percentage}% off`;
  if (promotion.amount) return `${formatCurrency(promotion.amount)} off`;
  return null;
};

export const getCatalogPromotionPresentation = (
  item: ShopItem,
): CatalogPromotionPresentation | null => {
  const originalPrice = item.originalPrice ?? item.price;
  const currentPrice = item.displayPrice ?? item.price;
  const savings = Math.max(0, originalPrice - currentPrice);
  const giftOffer = giftOfferForItem(item);
  if (giftOffer) {
    return {
      kind: "gift",
      promotionId: giftOffer.id,
      name: giftOffer.name,
      benefit: "Free",
      action: giftConditionPhrase(giftOffer),
    };
  }
  if (savings > 0 && item.bestPromotion) {
    return {
      kind: "applied",
      promotionId: item.bestPromotion.id,
      name: item.bestPromotion.name,
      benefit: `Save ${formatCurrency(savings)}`,
      action: "automatically at checkout",
    };
  }

  for (const promotion of item.availablePromotions ?? []) {
    const condition = catalogConditionSummary(promotion, item.id);
    const benefit = catalogConditionalBenefit(promotion);
    if (condition && benefit) {
      return {
        kind: "available",
        promotionId: promotion.id,
        name: promotion.name,
        benefit,
        action: `${condition} to unlock`,
      };
    }
  }
  return null;
};

export interface CatalogRequirementGroup {
  group: number;
  alternatives: string[];
}

export interface CatalogOfferAction {
  itemId: string;
  itemName: string;
  itemDescription: string | null;
  itemImage: string | null;
  itemPrice: number;
  quantity: number;
}

export interface CatalogOfferDetail {
  offerId: string;
  promotionId: string;
  name: string;
  description: string | null;
  benefit: string | null;
  requirementGroups: CatalogRequirementGroup[];
  relationshipSummary: string | null;
  actions: CatalogOfferAction[];
  isDisplayPriceOffer: boolean;
}

export interface CatalogPromotionDetails {
  applied: CatalogOfferDetail[];
  conditional: CatalogOfferDetail[];
  displaySavings: number;
}

const detailRequirementLabel = (
  condition: PublicShopPromotionCondition,
): string => {
  if (condition.ruleType === "MIN_SUBTOTAL") {
    return `Spend ${formatCurrency(condition.minSubtotalCents ?? 0)}`;
  }
  const quantity = condition.minQuantity ?? 1;
  if (condition.itemName) return `Buy ${quantity} ${condition.itemName}`;
  return `Add ${quantity} ${quantity === 1 ? "item" : "items"}`;
};

const detailRequirementGroups = (
  promotion: PublicShopPromotion,
): CatalogRequirementGroup[] => {
  if (!promotion.showConditionMessaging || promotion.conditions.length === 0) {
    return [];
  }
  return groupPromotionConditions(promotion.conditions)
    .map((conditions) => ({
      group: conditions[0]?.group ?? 0,
      alternatives: conditions.map(detailRequirementLabel),
    }))
    .sort((left, right) => left.group - right.group);
};

const catalogOfferBenefit = (
  promotion: PublicShopPromotion,
  itemName: string,
): string | null => {
  const target = promotion.targetDescription ?? itemName;
  const cap = promotion.quantity
    ? ` for up to ${promotion.quantity} ${promotion.quantity === 1 ? "unit" : "units"}`
    : "";
  if (promotion.percentage === 100) return `${target} free${cap}`;
  if (promotion.percentage) {
    return `${promotion.percentage}% off ${target}${cap}`;
  }
  if (promotion.amount) {
    return `${formatCurrency(promotion.amount)} off each ${target}${cap}`;
  }
  return null;
};

export const getCatalogPromotionDetails = (
  item: ShopItem,
  catalogItems: ShopItem[] = [],
): CatalogPromotionDetails => {
  const originalPrice = item.originalPrice ?? item.price;
  const currentPrice = item.displayPrice ?? item.price;
  const displaySavings = Math.max(0, originalPrice - currentPrice);
  const appliedOffers: PublicShopPromotion[] = [];
  const appliedOfferIds = new Set<string>();
  for (const offer of [
    item.bestPromotion,
    ...(item.applicablePromotions ?? []),
  ]) {
    if (!offer || appliedOfferIds.has(offer.offerId)) continue;
    appliedOfferIds.add(offer.offerId);
    appliedOffers.push(offer);
  }

  const catalogItemsById = new Map(
    catalogItems.map((catalogItem) => [catalogItem.id, catalogItem]),
  );
  const toDetail = (
    offer: PublicShopPromotion,
    isDisplayPriceOffer: boolean,
  ): CatalogOfferDetail => {
    const conditionItemIds = new Set(
      offer.showConditionMessaging
        ? offer.conditions.flatMap((condition) =>
            condition.itemId ? [condition.itemId] : [],
          )
        : [],
    );
    const currentIsTarget = offer.targetItemIds.includes(item.id);
    const currentIsCondition = conditionItemIds.has(item.id);
    const relatedItemIds = new Set<string>();
    if (currentIsTarget || !currentIsCondition) {
      conditionItemIds.forEach((itemId) => relatedItemIds.add(itemId));
    }
    if (
      offer.showConditionMessaging &&
      offer.targetItemIds.length === 1 &&
      (currentIsCondition || !currentIsTarget)
    ) {
      offer.targetItemIds.forEach((itemId) => relatedItemIds.add(itemId));
    }
    relatedItemIds.delete(item.id);

    const actions = Array.from(relatedItemIds).flatMap((itemId) => {
      const relatedItem = catalogItemsById.get(itemId);
      if (!relatedItem) return [];
      const condition = offer.conditions.find(
        (candidate) => candidate.itemId === itemId,
      );
      return [
        {
          itemId,
          itemName: relatedItem.name,
          itemDescription: relatedItem.description,
          itemImage: relatedItem.imageUrl,
          itemPrice: relatedItem.displayPrice ?? relatedItem.price,
          quantity: condition?.minQuantity ?? offer.quantity ?? 1,
        },
      ];
    });
    const conditionSummary = promotionConditionSummary(offer);
    const conditionalBenefit = catalogConditionalBenefit(offer);
    let relationshipSummary: string | null = null;
    if (actions.length > 0 && conditionSummary && conditionalBenefit) {
      relationshipSummary = currentIsCondition
        ? `${conditionSummary}, get ${conditionalBenefit}.`
        : `${conditionalBenefit} when you ${conditionSummary.charAt(0).toLowerCase()}${conditionSummary.slice(1)}.`;
    }

    return {
      offerId: offer.offerId,
      promotionId: offer.id,
      name: offer.name,
      description: offer.description,
      benefit: catalogOfferBenefit(offer, item.name),
      requirementGroups: detailRequirementGroups(offer),
      relationshipSummary,
      actions,
      isDisplayPriceOffer,
    };
  };

  const applied = appliedOffers.map((offer) =>
    toDetail(
      offer,
      displaySavings > 0 && offer.offerId === item.bestPromotion?.offerId,
    ),
  );
  const conditional: CatalogOfferDetail[] = [];
  const conditionalOfferIds = new Set<string>();
  for (const offer of item.availablePromotions ?? []) {
    if (
      appliedOfferIds.has(offer.offerId) ||
      conditionalOfferIds.has(offer.offerId)
    ) {
      continue;
    }
    const detail = toDetail(offer, false);
    if (detail.requirementGroups.length === 0) continue;
    conditionalOfferIds.add(offer.offerId);
    conditional.push(detail);
  }

  return { applied, conditional, displaySavings };
};

const unmetConditionStep = (
  condition: PublicShopPromotionCondition,
  lines: ShopCartLine[],
  subtotalLines: ShopCartLine[],
): string | null => {
  if (condition.ruleType === "MIN_SUBTOTAL") {
    const remaining = Math.max(
      0,
      (condition.minSubtotalCents ?? 0) - baseCartSubtotal(subtotalLines),
    );
    return remaining > 0 ? `spend ${formatCurrency(remaining)} more` : null;
  }

  const remaining = Math.max(
    0,
    (condition.minQuantity ?? 1) - conditionQuantity(condition, lines),
  );
  if (remaining === 0) return null;
  const itemName = condition.itemName ?? (remaining === 1 ? "item" : "items");
  return `add ${remaining} more ${itemName}`;
};

const unmetConditionGroups = (
  promotion: PublicShopPromotion,
  lines: ShopCartLine[],
  subtotalLines: ShopCartLine[] = lines,
): string[] => {
  const unmetGroups: string[] = [];
  for (const conditions of groupPromotionConditions(promotion.conditions)) {
    const steps = conditions.map((condition: PublicShopPromotionCondition) =>
      unmetConditionStep(condition, lines, subtotalLines),
    );
    if (steps.every(Boolean)) {
      unmetGroups.push(steps.join(" or "));
    }
  }
  return unmetGroups;
};

const withoutOneUnitOf = (
  lines: ShopCartLine[],
  itemId: string | null,
): ShopCartLine[] => {
  const index = lines.findIndex(
    (line) => "itemId" in line && line.itemId === itemId,
  );
  if (index === -1) return lines;
  const line = lines[index];
  return line.quantity > 1
    ? lines.map((other, i) =>
        i === index
          ? ({ ...line, quantity: line.quantity - 1 } as ShopCartLine)
          : other,
      )
    : lines.filter((_, i) => i !== index);
};

/** The free-item offer behind a cart line holding an item hidden from the shop. */
export const giftOfferForLine = (
  promotions: PublicShopPromotion[],
  line: ShopCartLine,
): PublicShopPromotion | null =>
  line.kind === "catalog-item"
    ? (promotions.find(
        (promotion) =>
          promotion.giftItemName && promotion.targetItemId === line.itemId,
      ) ?? null)
    : null;

/** Unmet steps toward a free item; like the server, its one free unit doesn't count toward a minimum spend. */
const unmetGiftConditionGroups = (
  offer: PublicShopPromotion,
  lines: ShopCartLine[],
): string[] =>
  unmetConditionGroups(
    offer,
    lines,
    withoutOneUnitOf(lines, offer.targetItemId),
  );

const giftOfferQualifies = (
  offer: PublicShopPromotion,
  lines: ShopCartLine[],
): boolean =>
  withoutOneUnitOf(lines, offer.targetItemId).length > 0 &&
  unmetGiftConditionGroups(offer, lines).length === 0;

/** Items whose one free unit the cart has qualified for. */
export const qualifiedGiftItemIds = (
  promotions: PublicShopPromotion[],
  lines: ShopCartLine[],
): Set<string> =>
  new Set(
    lines.flatMap((line) => {
      const offer = giftOfferForLine(promotions, line);
      return offer?.targetItemId && giftOfferQualifies(offer, lines)
        ? [offer.targetItemId]
        : [];
    }),
  );

/** Free-item lines the cart no longer qualifies for, or whose offer has left the catalog. */
export const unqualifiedGiftLineIds = (
  promotions: PublicShopPromotion[],
  lines: ShopCartLine[],
): string[] =>
  lines.flatMap((line) => {
    const offer = giftOfferForLine(promotions, line);
    if (!offer) {
      return line.kind === "catalog-item" && line.freeGift ? [line.lineId] : [];
    }
    return giftOfferQualifies(offer, lines) ? [] : [line.lineId];
  });

export const getShopPromotionMessages = (
  promotions: PublicShopPromotion[],
  lines: ShopCartLine[],
): ShopPromotionMessage[] => {
  if (lines.length === 0) return [];

  const result: ShopPromotionMessage[] = [];
  for (const promotion of promotions) {
    if (promotion.giftItemName) {
      if (giftOfferQualifies(promotion, lines)) {
        if (!lines.some((line) => giftOfferForLine([promotion], line))) {
          result.push({
            id: promotion.offerId,
            promotionId: promotion.id,
            text: `Unlocked: ${promotion.name}.`,
            status: "unlocked",
            giftOffer: promotion,
          });
        }
        continue;
      }
      if (!promotion.showConditionMessaging) continue;
      const unmetGroups = unmetGiftConditionGroups(promotion, lines);
      if (unmetGroups.length === 0) continue;
      const steps = unmetGroups.join("; and ");
      result.push({
        id: promotion.offerId,
        promotionId: promotion.id,
        text: `${steps.charAt(0).toUpperCase()}${steps.slice(1)} to get a free ${promotion.giftItemName}.`,
        status: "progress",
      });
      continue;
    }

    if (
      !promotion.showConditionMessaging ||
      promotion.conditions.length === 0 ||
      !promotionIsRelevant(promotion, lines)
    ) {
      continue;
    }

    const unmetGroups = unmetConditionGroups(promotion, lines);

    if (unmetGroups.length > 0) {
      const reward = promotionRewardLabel(promotion);
      result.push({
        id: promotion.offerId,
        promotionId: promotion.id,
        text: `To ${reward ? `get ${reward}` : `qualify for ${promotion.name}`}: ${unmetGroups.join("; and ")}.`,
        status: "progress",
      });
      continue;
    }

    if (!targetIsInCart(promotion, lines) && promotion.targetDescription) {
      result.push({
        id: promotion.offerId,
        promotionId: promotion.id,
        text: `Add ${promotion.targetDescription} to claim ${promotion.name}.`,
        status: "progress",
      });
      continue;
    }

    result.push({
      id: promotion.offerId,
      promotionId: promotion.id,
      text: `Unlocked: ${promotion.name}.`,
      status: "unlocked",
    });
  }
  return result;
};
